import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CreditCard, Info, PlugZap, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { driver, useAction, useBalance, useStations } from '@/api/driver'
import type { RfidCard, Wallet } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, StatusPill } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { connectorRate, connectorState, usableCard } from '@/lib/charging'
import { kw, npr } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { session } from '@/lib/session'

export const STARTING_KEY = 'saadhan.starting'

/** The wallet a card pays from, when we can tell: super cards use the Saadhan wallet, operator cards that operator's. */
export function walletFor(card: RfidCard, wallets: Wallet[], stationCpoId: number) {
  if (card.is_supercard) return wallets.find((w) => w.cpo_id == null) ?? null
  return wallets.find((w) => w.cpo_id === stationCpoId) ?? null
}

export function ChargePage() {
  const { chargerId = '', connector = '1' } = useParams()
  const { t } = useI18n()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const stations = useStations()
  const balance = useBalance()
  const [cardId, setCardId] = useState<number | null>(null)

  const start = useAction(({ card }: { card: number }) => driver.start(chargerId, Number(connector), card), {
    onSuccess: () => {
      try {
        sessionStorage.setItem(STARTING_KEY, JSON.stringify({ chargerId, at: Date.now(), userId: session.get()?.user.id }))
      } catch {
        /* private mode */
      }
      qc.invalidateQueries({ queryKey: ['active'] })
      qc.invalidateQueries({ queryKey: ['balance'] })
      navigate('/charging', { replace: true })
    },
  })

  if (stations.isLoading || balance.isLoading) return <LoadingBlock rows={4} />
  if (stations.error) return <ErrorState error={stations.error} onRetry={() => stations.refetch()} />
  if (balance.error) return <ErrorState error={balance.error} onRetry={() => balance.refetch()} />

  const station = stations.data?.stations.find((s) => s.chargers.some((c) => c.id.toLowerCase() === chargerId.toLowerCase()))
  const charger = station?.chargers.find((c) => c.id.toLowerCase() === chargerId.toLowerCase())
  const k = charger?.connectors.find((x) => x.connector_number === Number(connector))
  if (!station || !charger || !k) return <EmptyState icon={<PlugZap />} title={t('Connector not found')} action={<Link to="/" className="underline">{t('Open the map')}</Link>} />

  const st = connectorState(charger, k)
  const rate = connectorRate(k)
  const cards = (balance.data?.rfid_cards ?? []).filter(usableCard)
  const wallets = balance.data?.wallets ?? []
  const chosen = cards.find((c) => c.id === cardId) ?? (cards.length === 1 ? cards[0] : null)
  const wallet = chosen ? walletFor(chosen, wallets, station.cpo_id) : null

  return (
    <div className="mx-auto max-w-lg">
      <Link to={`/stations/${station.id}`} className="-ml-2 mb-3 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-text-2 hover:bg-surface-3">
        <ArrowLeft className="size-4" /> {station.name}
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">{t('Start charging')}</h1>

      <Card className="mt-4 p-4">
        <div className="flex items-start gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand-ink"><PlugZap className="size-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{k.connector_type} · {kw(k.max_power_kw ?? charger.max_power_kw)}</p>
            <p className="text-sm text-text-2">{t('Charger {id}, connector {n}', { id: charger.id, n: k.connector_number })}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <StatusPill tone={st.tone}>{t(st.label)}</StatusPill>
              <span className="font-medium">{rate != null ? `${npr(rate)} / kWh` : t('Time-of-day rate')}</span>
            </div>
          </div>
        </div>
      </Card>

      {!st.startable ? (
        <Card className="mt-4 p-4 text-sm text-text-2">{t('This connector cannot start a charge right now. Pick another one at the station.')}</Card>
      ) : cards.length === 0 ? (
        <NoCard />
      ) : (
        <>
          <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Pay with')}</h2>
          <div className="space-y-2" role="radiogroup" aria-label={t('Pay with')}>
            {cards.map((c) => {
              const w = walletFor(c, wallets, station.cpo_id)
              const on = chosen?.id === c.id
              return (
                <button key={c.id} role="radio" aria-checked={on} onClick={() => setCardId(c.id)} className={cn('flex w-full items-center gap-3 rounded-xl border p-3 text-left', on ? 'border-brand bg-brand-soft/60' : 'border-border bg-surface hover:bg-surface-2')}>
                  <CreditCard className="size-5 shrink-0 text-text-2" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-sm font-medium">{c.card_number}</span>
                    <span className="block text-xs text-text-2">{c.is_supercard ? t('Saadhan super card · works at every station') : t('Operator card')}</span>
                  </span>
                  {w && <span className="text-right text-sm"><span className="block text-xs text-text-2">{t('Balance')}</span><span className="tabular font-semibold">{npr(w.balance)}</span></span>}
                </button>
              )
            })}
          </div>
          {chosen && !wallet && !chosen.is_supercard && (
            <p className="mt-2 flex gap-2 text-xs text-text-2"><Info className="size-4 shrink-0" />{t('An operator card only works at that operator’s stations and pays from your wallet with them.')}</p>
          )}

          <Card className="mt-6 p-4">
            <ol className="space-y-2 text-sm">
              <li className="flex gap-2"><span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 text-xs font-semibold">1</span>{t('Park and plug the cable into your car.')}</li>
              <li className="flex gap-2"><span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 text-xs font-semibold">2</span>{t('Press Start. The charger begins within a few seconds.')}</li>
              <li className="flex gap-2"><span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-3 text-xs font-semibold">3</span>{t('You pay for the energy used. Stop any time from this site.')}</li>
            </ol>
          </Card>

          <div className="pb-safe sticky bottom-16 mt-6 md:bottom-4">
            <Button size="lg" variant="brand" className="h-12 w-full text-base shadow-card" disabled={!chosen} loading={start.isPending} onClick={() => chosen && start.mutate({ card: chosen.id })}>
              <Zap className="fill-current" /> {t('Start charging')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function NoCard() {
  const { t } = useI18n()
  return (
    <Card className="mt-6 p-4">
      <p className="font-semibold">{t('You need a charging card first')}</p>
      <p className="mt-1 text-sm text-text-2">{t('Charging is paid from a wallet linked to an RFID card. Ask the staff at any station for a card and to add credit; it is linked to your phone number and works here straight away.')}</p>
      <Link to="/wallet" className="mt-3 inline-block text-sm font-medium underline">{t('See your wallet')}</Link>
    </Card>
  )
}
